import { ActionContext, ActionTree } from 'vuex';
import { MetaAttributes, UtilityState } from '@/store/modules/utility/state';
import { RootState } from '@/store/state';
import store from '@/store/index';
import { CampaignState } from '@/models/CampaignModel';

export type UtilityActionContext = ActionContext<UtilityState, RootState>;

export type Actions = {
  loadFont(context: UtilityActionContext, fontFamily: string): void;
  addBodyClass(context: UtilityActionContext, className: string): void;
  removeBodyClass(context: UtilityActionContext, className: string): void;
  addMetatag(context: UtilityActionContext, metaAttributes: MetaAttributes): void;
};

export const actions: ActionTree<UtilityState, RootState> & Actions = {
  loadFont: ({ commit, state }, fontFamily: string) => {
    const isCustom = fontFamily.includes('custom:');
    if (isCustom) return;

    // Sometimes we might get font family forwarded with multiple fonts in them.
    // Here we need to destruct the font family using regex and find our font we need to get.
    if (fontFamily.includes('"')) {
      const match = fontFamily.match(/"([a-z_\-0-9 ]+)"/i);

      if (match && match[1]) {
        fontFamily = match[1];
      }
    }

    const campaignState = store.getters['campaign/state'] as CampaignState;
    if (campaignState.config && campaignState.config.customFonts && Array.isArray(campaignState.config.customFonts)) {
      const doesFontExist = campaignState.config?.customFonts.find((font) => {
        return font.fontFamily === fontFamily;
      });
      if (doesFontExist) return;
    }

    const fonts = state.fonts ?? [];

    if (fonts.indexOf(fontFamily) === -1) {
      fonts.push(fontFamily);
      commit('setFonts', fonts);
    }
  },
  addBodyClass: ({ commit, state }, className) => {
    const bodyClassList = state.bodyClassList ?? [];

    if (bodyClassList.indexOf(className) === -1) {
      bodyClassList.push(className);
      commit('setBodyClassList', bodyClassList);
    }
  },
  removeBodyClass: ({ commit, state }, className) => {
    let bodyClassList = state.bodyClassList ?? [];

    if (bodyClassList.indexOf(className) !== -1) {
      bodyClassList = bodyClassList.filter((bodyClassName) => bodyClassName !== className);
      commit('setBodyClassList', bodyClassList);
    }
  },
  addMetatag: ({ commit, state }, metaAttributes: MetaAttributes) => {
    const metatags = state.metatags ?? [];

    const existingEntry = metatags.find((tag) => {
      for (const key in tag) {
        if (Object.prototype.hasOwnProperty.call(tag, key)) {
          if (tag[`${key}`] === metaAttributes[`${key}`]) {
            return metaAttributes;
          }
        }
      }
    });

    if (!existingEntry) {
      metatags.push(metaAttributes);
      commit('setMetatags', metatags);
    } else {
      metatags.push(existingEntry);
      commit('setMetatags', metatags);
    }
  }
};
